import { SyntheticEvent } from 'react';

import { DutyHourType, DutyHourTypeDynFlag, DutyHourTypeFlag } from 'generated';
import { matchSorter } from 'match-sorter';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';

import { queries } from 'RestAPIs/rapini';

import { TextField } from '@mui/material';

import { VirtualizedAutocomplete } from 'components/shared';

import LineClamp from './LineClamp';
import {
  GridContainer,
  GridCompact4,
  GridCompact8
} from './Grid/Grid.components';

type Props = {
  field?: ControllerRenderProps<any, string>;
  fieldState?: ControllerFieldState;
  filterDutyHourTypeDynFlags?: DutyHourTypeDynFlag[];
  filterDutyHourTypeFlags?: DutyHourTypeFlag[];
  fullWidth?: boolean;
  label: string;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    dht?: DutyHourType | DutyHourType[]
  ) => void;
  referenceDate?: Date;
  required?: boolean;
  value?: number;
  variant?: 'standard' | 'filled' | 'outlined';
  checkRestrictedAuthorization?: boolean;
  disabled?: boolean;
};

export type Option = {
  id?: number;
  value?: number;
  label: string;
  description?: string;
  code?: string;
};

const DutyHourTypeSelector = (props: Props) => {
  const {
    field,
    fieldState,
    filterDutyHourTypeDynFlags,
    filterDutyHourTypeFlags,
    fullWidth,
    label,
    onChange,
    referenceDate,
    required,
    value,
    variant = 'outlined',
    checkRestrictedAuthorization,
    disabled
  } = props;
  const renderOption = (
    props: React.HTMLProps<HTMLLIElement>,
    option: Option
  ) => {
    const { key: _key, ...restProps } = props;

    return (
      <li key={option.id} {...restProps}>
        <GridContainer alignItems="center" width={1}>
          <GridCompact4 className="code">{option.code}</GridCompact4>
          <GridCompact8 className="description" title={option.description}>
            <LineClamp lineClamp={2} text={option.description ?? ''} />
          </GridCompact8>
        </GridContainer>
      </li>
    );
  };

  const { data: dutyHourTypes } = queries.useGetDutyHourTypes(
    referenceDate?.toISOString(),
    checkRestrictedAuthorization,
    {
      staleTime: 30 * 60 * 1000, // stale for 30 minutes
      cacheTime: 60 * 60 * 1000, // cache for 1 hour
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  );

  function filterFlags(dutyHourTypes: DutyHourType[]) {
    const filteredOne = filterDutyHourTypeDynFlags
      ? dutyHourTypes.filter((dht) =>
          filterDutyHourTypeDynFlags.every(
            (flag) => dht.dutyHourTypeDynFlags?.includes(flag)
          )
        )
      : dutyHourTypes;
    return filterDutyHourTypeFlags
      ? dutyHourTypes.filter((dht) =>
          filterDutyHourTypeFlags.every(
            (flag) => dht.dutyHourTypeFlags?.includes(flag)
          )
        )
      : filteredOne;
  }

  const options = filterFlags(dutyHourTypes ?? []).map((dht) => ({
    label: dht.description || dht.code,
    description: dht.description,
    code: dht.code,
    id: dht.dutyHourTypeId,
    value: dht.dutyHourTypeId
  }));

  const filterOptions = (options: Option[], { inputValue }) =>
    matchSorter(options, inputValue, { keys: ['label', 'description'] });

  const onOptionChange = (
    event: SyntheticEvent<Element, Event>,
    value: NonNullable<string | Option> | (string | Option)[] | null
  ) => {
    if (typeof value === 'string') {
      onChange &&
        onChange(
          event,
          dutyHourTypes?.find((dht) => dht.dutyHourTypeId === +value)
        );
    } else if (Array.isArray(value)) {
      const ids = value.map((v) => (typeof v === 'string' ? +v : v.id));
      onChange &&
        onChange(
          event,
          dutyHourTypes?.filter((dht) => ids.indexOf(dht.dutyHourTypeId) > -1)
        );
    } else {
      const dht = dutyHourTypes?.find(
        (dht) => dht.dutyHourTypeId === value?.id
      );
      field?.onChange(dht?.dutyHourTypeId ?? -1);
    }
  };

  return (
    <VirtualizedAutocomplete
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size="small"
          label={label}
          required={required}
          fullWidth={fullWidth}
          error={!!fieldState?.error?.type}
          helperText={fieldState?.error?.message}
        />
      )}
      options={options}
      getItemSize={() => 60}
      renderOption={renderOption}
      value={options?.find(({ id }) => id === (field?.value || value)) ?? null}
      onChange={onOptionChange}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option: Option, value: Option) => {
        return option.id === value?.id;
      }}
      disabled={disabled}
    />
  );
};

export default DutyHourTypeSelector;
